//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import FileId from '@models/FileId';
import { GraphApiUserInsightsPeriod } from '@models/GraphApiUserInsightsPeriod';
import {
  PostsStatAggregationArray,
  PostsStatHistogramArray,
} from '@models/IAggregationResult';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import InstagramBusinessAccountId from '@models/InstagramBusinessAccountId';
import InstagramUsername from '@models/InstagramUsername';
import Interval from '@models/Interval';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';

export interface IDefaultInstagramUserResponse {
  user: IInstagramGraphApiUser;
}

interface IInstagramGraphApiUser {
  userId: InstagramBusinessAccountId;
  lastUpdated?: number;
  bio?: string;
  fullName?: string;
  profileImageUrl?: string;
  profileImageFileId?: FileId;
  followerCount?: number;
  followingCount?: number;
  photoCount?: number;
  username?: InstagramUsername;
  website?: string;
  isBusiness?: boolean;
}

export interface IDefaultInstagramUserMediaRequest {
  startTime?: number;
  endTime?: number;
  limit?: number;
  offset?: number;
  sortKey?: UniversalSearchSortKey;
  sortDirection?: UniversalSearchSortDirection;
  labels?: string[];
  includeCopyrightedPosts?: boolean;
  engagementPostFilter?: EngagementPostType;
}

export interface IExportInstagramEngagementPostPerformanceRequestBody {
  startTime: number;
  endTime: number;
  sortKey: UniversalSearchSortKey;
  sortDirection: UniversalSearchSortDirection;
  labels?: string[];
  engagementPostFilter?: EngagementPostType;
}

export enum EngagementPostType {
  All = 'All',
  Images = 'Images',
  Videos = 'Videos',
  Carousels = 'Carousels',
  IGTV = 'IGTV',
  Reels = 'Reels',
}

export interface IDefaultInstagramUserPostsResponse {
  total: number;
  items: IInstagramPost[];
}

export interface IInstagramDefaultUserLivePostsRequest {
  limit?: number;
}

export interface IInstagramDefaultUserPostsStatsRequest {
  startTime?: number;
  endTime?: number;
  labels?: string[];
  includeCopyrightedPosts?: boolean;
  engagementPostFilter?: EngagementPostType;
}

export interface IInstagramDefaultUserPostsLabelsRequest {
  startTime?: number;
  endTime?: number;
  limit?: number;
}

export interface IInstagramDefaultUserPostsLabelsAutocompleteRequest {
  searchQuery: string;
  startTime?: number;
  endTime?: number;
}

export interface IInstagramDefaultUserPostsStatsResponse {
  stats: PostsStatAggregationArray;
  comparisonStats: PostsStatAggregationArray;
  histogram: PostsStatHistogramArray;
}

export interface IInstagramDefaultUserPostsLabelsResponse {
  data: IInstagramDefaultUserPostsLabelsResult;
}

export interface IInstagramDefaultUserPostsLabelsResult {
  labels: Record<string, number>;
}

export interface IInstagramDefaultUserPostsLabelsAutocompleteResponse {
  data: IInstagramDefaultUserPostsLabelsAutocompleteResult;
}

export interface IInstagramDefaultUserPostsLabelsAutocompleteResult {
  labels: [string, number][];
}

export interface IDefaultInstagramEngagersRequest {
  interval?: Interval;
  limit?: number;
}

export interface IDefaultInstagramEngagersResponse {
  engagers: IInstagramEngager[];
}

export interface IInstagramEngager {
  userId?: InstagramBusinessAccountId;
  lastUpdated?: number;
  bio?: string;
  fullName?: string;
  profileImageUrl?: string;
  profileImageFileId?: FileId;
  followerCount?: number;
  followingCount?: number;
  recentLikeCount?: number;
  photoCount?: number;
  username?: string;
  website?: string;
  isBusiness?: boolean;
}

// While the endpoint which relies on this request will soon be deleted,
// methods which take the interface below as an argument have yet to be replaced.
export interface IDefaultGraphApiUserInsightsRequest {
  interval: Interval;
  period: GraphApiUserInsightsPeriod[];
}
